/**
 * useCamera — usePdf417Detection feature hook
 *
 * Continuous PDF417 barcode detection from a live video stream. Uses the
 * native BarcodeDetector API (preferred — fast, built into Chrome/Android
 * WebView) with automatic ZXing fallback if BarcodeDetector doesn't find
 * any barcodes after a reasonable trial period.
 *
 * Designed to be used alongside useCamera + CameraModal. The hook watches
 * the video element from useCamera and calls `onResult` when a barcode is
 * decoded. Detection is automatically paused when `enabled` is false.
 *
 * IMPORTANT: The effect depends on `[enabled]` only — NOT on `stream`.
 * The `stream` prop is tracked via a ref. This is critical because
 * `useCamera.startCameraInternal()` calls `setStream(null)` then
 * `setStream(newStream)` when restarting the camera, which would tear
 * down and restart the detection loop if `stream` were in the dependency
 * array. The detect loop polls the video element's `readyState` directly
 * and naturally handles stream changes without restarting.
 *
 * Usage:
 * ```tsx
 * const camera = useCamera({ ... });
 * usePdf417Detection({
 *   videoRef: camera.videoRef,
 *   stream: camera.stream,
 *   enabled: camera.isOpen && !paused,
 *   formats: ['pdf417', 'qr_code', 'data_matrix'],
 *   onResult: (rawText) => console.log('Decoded:', rawText),
 * });
 * return <CameraModal camera={camera} darkModeEnhance ... />;
 * ```
 */

import { useEffect, useRef } from 'react';

export interface UsePdf417DetectionParams {
  /** Video element ref from useCamera */
  videoRef: React.RefObject<HTMLVideoElement | null>;
  /** Media stream from useCamera (null when camera is off) */
  stream: MediaStream | null;
  /** Whether detection is active (default: true when stream is available) */
  enabled?: boolean;
  /** Barcode formats to detect (default: pdf417, qr_code, data_matrix, code_128, code_39, ean_13) */
  formats?: string[];
  /**
   * Called when a barcode is successfully decoded.
   * `rawText` is the decoded string (may lose binary data for non-text barcodes).
   * `rawBytes` is the raw byte array when available (ZXing provides this;
   * BarcodeDetector falls back to Latin-1 encoding of rawText).
   */
  onResult: (rawText: string, rawBytes?: Uint8Array) => void;
}

const DEFAULT_FORMATS = ['pdf417', 'qr_code', 'data_matrix', 'code_128', 'code_39', 'ean_13'];

/** Number of BarcodeDetector ticks to try before falling back to ZXing */
const BARCODE_DETECTOR_TRIAL_TICKS = 30; // ~6 seconds at 200ms/tick

/**
 * Compare two format arrays by content (not identity).
 * Returns true if they contain the same strings in the same order.
 */
function formatsEqual(a: string[] | undefined, b: string[] | undefined): boolean {
  if (a === b) return true;
  if (!a || !b) return a === b;
  if (a.length !== b.length) return false;
  return a.every((f, i) => f === b[i]);
}

export function usePdf417Detection({
  videoRef,
  stream,
  enabled = true,
  formats,
  onResult,
}: UsePdf417DetectionParams) {
  const detectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const detectorRef = useRef<{ detect: (source: HTMLVideoElement) => Promise<{ rawValue: string; format: string }[]> } | null>(null);
  const codeReaderRef = useRef<{ stopAsyncDecode: () => void; stopContinuousDecode?: () => void } | null>(null);
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;

  // ── Refs to avoid effect re-runs ──
  // Both `formats` and `stream` are tracked via refs. The effect depends
  // on `[enabled]` only. This prevents the detection loop from being
  // torn down and restarted when `stream` changes (which happens when
  // useCamera.startCameraInternal() calls setStream(null) → setStream(newStream)).
  const formatsRef = useRef<string[] | undefined>(formats);
  if (!formatsEqual(formatsRef.current, formats)) {
    formatsRef.current = formats;
  }

  const streamRef = useRef<MediaStream | null>(stream);
  streamRef.current = stream;

  useEffect(() => {
    if (!enabled) {
      console.log('[usePdf417Detection] Effect skipped: enabled=false');
      return;
    }

    let cancelled = false;
    let detectCount = 0;
    let detectorStarted = false;
    let zxingStarted = false;

    console.log('[usePdf417Detection] Effect starting — enabled=true, polling for video readiness');

    // ── Helper: start ZXing continuous decode ──
    const startZXing = async () => {
      if (zxingStarted || cancelled || !videoRef.current) return;
      zxingStarted = true;

      // Stop the BarcodeDetector if it was running
      detectorRef.current = null;

      try {
        const { BrowserMultiFormatReader, DecodeHintType, BarcodeFormat } = await import('@zxing/library');
        if (cancelled || !videoRef.current) return;

        console.log('[usePdf417Detection] ZXing loaded, starting continuous decode');

        const targetFormats = formatsRef.current ?? DEFAULT_FORMATS;
        const hints = new Map();
        const zxingFormats = targetFormats.map(f => {
          const mapping: Record<string, number> = {
            pdf417: BarcodeFormat.PDF_417,
            qr_code: BarcodeFormat.QR_CODE,
            data_matrix: BarcodeFormat.DATA_MATRIX,
            code_128: BarcodeFormat.CODE_128,
            code_39: BarcodeFormat.CODE_39,
            ean_13: BarcodeFormat.EAN_13,
            ean_8: BarcodeFormat.EAN_8,
            itf: BarcodeFormat.ITF,
            codabar: BarcodeFormat.CODABAR,
            aztec: BarcodeFormat.AZTEC,
            upc_a: BarcodeFormat.UPC_A,
            upc_e: BarcodeFormat.UPC_E,
            code_93: BarcodeFormat.CODE_93,
          };
          return mapping[f] ?? BarcodeFormat.PDF_417;
        });
        hints.set(DecodeHintType.POSSIBLE_FORMATS, zxingFormats);
        hints.set(DecodeHintType.TRY_HARDER, true);

        const codeReader = new BrowserMultiFormatReader(hints);
        // Set the video element so ZXing's internal state is correct
        (codeReader as unknown as { videoElement: HTMLVideoElement }).videoElement = videoRef.current;
        codeReaderRef.current = codeReader;

        // Use decodeContinuously directly — bypasses playVideoOnLoadAsync
        // which hangs if the video is already playing.
        codeReader.decodeContinuously(videoRef.current, (result, error) => {
          if (cancelled) return;
          if (error) {
            detectCount++;
            if (detectCount % 30 === 1) {
              console.log('[usePdf417Detection] ZXing scanning... tick #', detectCount);
            }
            return;
          }
          if (result && result.getText()) {
            console.log('[usePdf417Detection] ZXing detected barcode! text length:', result.getText().length);
            const text = result.getText();
            const rawBytes = result.getRawBytes();
            let bytes: Uint8Array | undefined;
            if (rawBytes) {
              bytes = new Uint8Array(rawBytes);
            } else if (result.getBarcodeFormat() === BarcodeFormat.PDF_417) {
              // ZXing's PDF417 DecoderResult always has rawBytes=null.
              // Recover the source bytes from the text: byte-compaction
              // segments are decoded via ISO-8859-1, so each char code
              // maps 1:1 back to the original byte.
              bytes = new Uint8Array(text.length);
              for (let i = 0; i < text.length; i++) {
                bytes[i] = text.charCodeAt(i) & 0xff;
              }
            }
            onResultRef.current(text, bytes);
          }
        });
        console.log('[usePdf417Detection] ZXing continuous decode started');
      } catch (err) {
        console.error('[usePdf417Detection] ZXing fallback failed:', err);
      }
    };

    // ── Polling detect loop ──
    // Instead of starting detection immediately, we poll the video element's
    // readyState. When it's ready (>= 2), we create the detector and start
    // detecting. If the stream changes (readyState drops), we just keep
    // polling — no teardown/restart needed.
    const pollLoop = async () => {
      if (cancelled) return;

      const currentVideo = videoRef.current;
      if (!currentVideo) {
        if (detectCount === 0) console.log('[usePdf417Detection] No video element yet, waiting...');
        detectCount++;
        if (!cancelled) {
          detectTimeoutRef.current = setTimeout(pollLoop, 200);
        }
        return;
      }

      // Self-healing: if srcObject is not set, try to attach from stream ref
      if (!currentVideo.srcObject && streamRef.current) {
        console.log('[usePdf417Detection] srcObject is null — re-attaching stream from ref');
        currentVideo.srcObject = streamRef.current;
        void currentVideo.play().catch((playErr) => {
          console.warn('[usePdf417Detection] play() failed after re-attaching stream:', playErr);
        });
      }

      // Wait for video to be ready
      if (currentVideo.readyState < 2 || !currentVideo.videoWidth) {
        detectCount++;
        if (detectCount === 1) {
          console.log('[usePdf417Detection] Video not ready, waiting...', {
            readyState: currentVideo.readyState,
            videoWidth: currentVideo.videoWidth,
            hasSrcObject: !!currentVideo.srcObject,
          });
        }
        if (!cancelled) {
          detectTimeoutRef.current = setTimeout(pollLoop, 200);
        }
        return;
      }

      // ── Video is ready — start detection if not already started ──
      if (!detectorStarted) {
        detectorStarted = true;
        console.log('[usePdf417Detection] Video ready! Starting detection...', {
          readyState: currentVideo.readyState,
          videoWidth: currentVideo.videoWidth,
          videoHeight: currentVideo.videoHeight,
        });

        const targetFormats = formatsRef.current ?? DEFAULT_FORMATS;

        // ── Method 1: BarcodeDetector API (preferred — native, fast) ──
        const win = typeof window !== 'undefined' ? window as Window & {
          BarcodeDetector?: new (options?: { formats: string[] }) => {
            detect: (source: HTMLVideoElement) => Promise<{ rawValue: string; format: string }[]>;
          };
        } : undefined;

        if (win?.BarcodeDetector) {
          console.log('[usePdf417Detection] BarcodeDetector API available, formats:', targetFormats);

          // Log supported formats for diagnostics
          let supportedFormats: string[] | undefined;
          const ctor = win.BarcodeDetector as unknown as { getSupportedFormats?: () => Promise<string[]> };
          if (typeof ctor.getSupportedFormats === 'function') {
            try {
              supportedFormats = await ctor.getSupportedFormats();
              console.log('[usePdf417Detection] BarcodeDetector supported formats on this device:', supportedFormats);
            } catch {
              // getSupportedFormats() can fail — not critical
            }
          }

          // If the native detector can't decode pdf417 (common on desktop
          // WebViews and some Android builds), skip the multi-second
          // BarcodeDetector trial entirely and go straight to ZXing.
          if (supportedFormats && targetFormats.includes('pdf417') && !supportedFormats.includes('pdf417')) {
            console.log('[usePdf417Detection] BarcodeDetector lacks pdf417 support on this device — using ZXing directly');
            await startZXing();
            return;
          }

          let detector: { detect: (source: HTMLVideoElement) => Promise<{ rawValue: string; format: string }[]> } | null = null;
          try {
            detector = new win.BarcodeDetector({ formats: targetFormats });
            console.log('[usePdf417Detection] BarcodeDetector created with full format list');
          } catch (err) {
            console.warn('[usePdf417Detection] BarcodeDetector constructor failed with formats:', targetFormats, err);
            try {
              detector = new win.BarcodeDetector();
              console.log('[usePdf417Detection] BarcodeDetector created with no format filter (detect all)');
            } catch (err2) {
              console.warn('[usePdf417Detection] BarcodeDetector constructor failed even without formats:', err2);
            }
          }

          if (detector) {
            if (cancelled) return;
            detectorRef.current = detector;

            let bdDetectCount = 0;

            // ── BarcodeDetector detect loop ──
            // Runs for BARCODE_DETECTOR_TRIAL_TICKS attempts. If no barcodes
            // are found, falls back to ZXing (some Capacitor WebViews have
            // BarcodeDetector but it doesn't actually decode anything).
            const bdDetectLoop = async () => {
              if (cancelled || !videoRef.current) return;
              bdDetectCount++;
              detectCount++;

              // Log video state every 30 ticks
              if (bdDetectCount % 30 === 1) {
                console.log('[usePdf417Detection] BarcodeDetector tick #', bdDetectCount, {
                  readyState: videoRef.current.readyState,
                  videoWidth: videoRef.current.videoWidth,
                  paused: videoRef.current.paused,
                });
              }

              // If video became not ready, pause but keep polling
              if (videoRef.current.readyState < 2 || !videoRef.current.videoWidth) {
                console.log('[usePdf417Detection] Video became unready, pausing detection...');
                if (!cancelled) {
                  detectTimeoutRef.current = setTimeout(bdDetectLoop, 200);
                }
                return;
              }

              try {
                const barcodes = await detector!.detect(videoRef.current);
                if (cancelled) return;
                if (barcodes.length > 0 && barcodes[0].rawValue) {
                  console.log('[usePdf417Detection] Barcode detected via BarcodeDetector! format:', barcodes[0].format, 'text length:', barcodes[0].rawValue.length);
                  const text = barcodes[0].rawValue;
                  const bytes = new Uint8Array(text.length);
                  for (let i = 0; i < text.length; i++) {
                    bytes[i] = text.charCodeAt(i) & 0xff;
                  }
                  onResultRef.current(text, bytes);
                  return;
                }
              } catch (err) {
                if (bdDetectCount === 1) console.log('[usePdf417Detection] detect() threw (expected during startup):', err);
              }

              // If BarcodeDetector hasn't found anything after trial period,
              // fall back to ZXing
              if (bdDetectCount >= BARCODE_DETECTOR_TRIAL_TICKS) {
                console.warn('[usePdf417Detection] BarcodeDetector found no barcodes after', BARCODE_DETECTOR_TRIAL_TICKS, 'ticks — falling back to ZXing');
                await startZXing();
                return;
              }

              if (!cancelled) {
                detectTimeoutRef.current = setTimeout(bdDetectLoop, 200);
              }
            };
            bdDetectLoop();
            return;
          }
        } else {
          console.log('[usePdf417Detection] BarcodeDetector API not available, using ZXing directly');
        }

        // ── Method 2: ZXing fallback (or primary if no BarcodeDetector) ──
        await startZXing();
        return;
      }

      // Detector already started — this shouldn't happen because the detect
      // loop schedules itself. But just in case, poll again.
      if (!cancelled) {
        detectTimeoutRef.current = setTimeout(pollLoop, 200);
      }
    };

    pollLoop();

    return () => {
      console.log('[usePdf417Detection] Effect cleanup — stopping detection (detectCount:', detectCount, ')');
      cancelled = true;
      if (detectTimeoutRef.current) {
        clearTimeout(detectTimeoutRef.current);
        detectTimeoutRef.current = null;
      }
      if (codeReaderRef.current) {
        try {
          codeReaderRef.current.stopContinuousDecode?.();
          codeReaderRef.current.stopAsyncDecode();
        } catch { /* ignore */ }
        codeReaderRef.current = null;
      }
      detectorRef.current = null;
    };
    // NOTE: `stream` and `formats` are intentionally omitted — they're
    // tracked via refs to avoid tearing down the detection loop when
    // the stream changes (useCamera calls setStream(null) → setStream(newStream)
    // when restarting the camera).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, videoRef]);
}
